import React from "react";
import { LABELS } from "../../../../constants/labels/jetlift/jetlift.labels";
import Accordion from "../../../common/accordion";
import TableComponent from "../../../common/TableComponent";
import { useAppSelector } from "../../../../store/hooks/StoreHooks";
import useJetliftCollapseFacade from "../../../../facade/jetlift/collapse/collapse.facade";
import IPRCurveGraph from "../../../calculation-section/basiccalculations/charts/wellInfo-curve";
import DepletionCurveGraph from "../../../calculation-section/basiccalculations/charts/depletion-curve";
import IPRTable from "../../../calculation-section/basiccalculations/inflow-calc-table";
import useOutputValidations from "../OutputValidations";
import useJetliftBasicCalculationsFacade from "../../../../facade/jetlift/jetlift-output/basicCalculation.facade";
import { columns, defaultDepletionData, defaultIPRData, tableColumns } from "../../../../constants/jetlift/jetlift";

const BasicCalculations: React.FC = () => {
    const { sectionsData, renderFields, renderExportFields, iprChartRef, depletionChartRef } = useOutputValidations();
    const { wellInflowCalculationsCollapse, wellIprCalculationsCollapse, summaryCollapse, basicPerformanceCurveCollapse } = useAppSelector((state) => state?.jetlift?.collapse);
    const { wellInflowData, IPRSelection, iprCurve, depletionCurve, basicSummary, basicSummaryScenarios, formattedBasicSummaryScenarios } = useAppSelector((state) => state?.jetlift?.basicCalculations);
    const { wellInput } = useAppSelector((state) => state?.jetlift?.well);
    const { jetPump } = useAppSelector((state) => state?.jetlift?.jetPump);
    const { gasIntakeData } = useAppSelector((state) => state?.jetlift?.gasCalculations);
    const { updateWellInflowCalculationsCollapse, updateWellIprCalculationsCollapse, updateSummaryCollapse, updateBasicPerformanceCurveCollapse } = useJetliftCollapseFacade();
    const { updateIPRSelection } = useJetliftBasicCalculationsFacade();

    const tableData: any = [
        {
            label: <div className="text-primary-400 text-[14px] font-semibold">{`Custom Nozzle (${jetPump?.nozzleDiameterSize}) and Throat (${jetPump?.throatDiameterSize}) diameters Pump Performance Summary.`}</div>,
        },
        {
            label: `${LABELS?.targetProductionRate}: ${wellInput?.productionRate} ${LABELS?.blpd} @ ${LABELS?.pumpIntakePressure}: ${basicSummary?.pumpIntakePressure !== "NaN" ? basicSummary?.pumpIntakePressure : ""} ${LABELS?.psia}`,
        },
        {
            label: `${LABELS?.predictedSurfacePowerFluidInjectionPressure} (${LABELS?.psia})`,
            Q0: `${basicSummary?.surfacePowerFluidInjectionPressure !== "NaN" ? Number(basicSummary?.surfacePowerFluidInjectionPressure)?.toFixed(2) : "0"}`,
        },
        {
            label: `${LABELS?.predictedSurfacePowerFluidInjectionRate} (${LABELS?.blpd})`,
            Q0: `${basicSummary?.surfacePowerFluidInjectionRate !== "NaN" ? Number(basicSummary?.surfacePowerFluidInjectionRate)?.toFixed(2) : "0"}`,
        },
        {
            label: `${LABELS?.predictedPumpIntakePressure} (${LABELS?.psia})`,
            Q0: `${basicSummary?.pumpIntakePressure !== "NaN" ? Number(basicSummary?.pumpIntakePressure)?.toFixed(2) : "0"}`,
        },
        {
            label: `${LABELS?.predictedPumpDischargePressure} (${LABELS?.psia})`,
            Q0: `${basicSummary?.pumpDischargePressure !== "NaN" ? Number(basicSummary?.pumpDischargePressure)?.toFixed(2) : "0"}`,
        },
        {
            label: `${LABELS?.predictedPowerFluidPressureAtPump} (${LABELS?.psia})`,
            Q0: `${basicSummary?.powerFluidPressureAtPump !== "NaN" ? Number(basicSummary?.powerFluidPressureAtPump)?.toFixed(2) : "0"}`,
        },
        {
            label: `${LABELS?.predictedHorsePowerRequirement} (${LABELS?.hp})`,
            Q0: `${basicSummary?.horsePowerRequirement !== "NaN" ? Number(basicSummary?.horsePowerRequirement)?.toFixed(2) : "0"}`,
        }
    ];

    const hpData = basicSummaryScenarios?.length > 0 ? [
        {
            label: `${LABELS?.maxHPRequirement}`,
            ...Object.fromEntries(
                basicSummaryScenarios?.map((item: any, index: any) => [`value${index + 1}`, item?.hhp?.toFixed(2)])
            ),
        }
    ] : [];

    const handleRadioChange = (e: any) => {
        updateIPRSelection(e?.target?.value);
    };

    return (
        <div className="text-neutral-25 flex flex-col gap-[16px]" role="basic-calculations" aria-roledescription="basic-calculations">
            <span className="text-[18px] font-medium text-neutral-25">Basic calculations</span>
            <Accordion
                title={LABELS?.summary}
                isOpen={summaryCollapse}
                onToggle={() => updateSummaryCollapse(!summaryCollapse)}
                content={
                    <div className="flex flex-col gap-[12px] p-[12px]">
                        <TableComponent data={tableData} columns={tableColumns} rowsPerPage={1} showHeader={false} />
                        <TableComponent
                            data={formattedBasicSummaryScenarios || []}
                            columns={columns}
                            rowsPerPage={1}
                            showHeader={false}
                        />
                        <TableComponent
                            data={hpData}
                            columns={columns}
                            rowsPerPage={1}
                            showHeader={false}
                        />
                    </div>
                }
            />
            <Accordion
                title={LABELS?.wellInflowCals}
                isOpen={wellInflowCalculationsCollapse}
                onToggle={() => updateWellInflowCalculationsCollapse(!wellInflowCalculationsCollapse)}
                content={
                    <div className="p-[16px]">
                        <IPRTable wellInflowData={wellInflowData || []} iprSelected={IPRSelection} onRadioChange={(e) => handleRadioChange(e)} />
                    </div>
                }
            />
            <Accordion
                title={LABELS?.wellIPRCals}
                isOpen={wellIprCalculationsCollapse}
                onToggle={() => updateWellIprCalculationsCollapse(!wellIprCalculationsCollapse)}
                content={(
                    <div className="p-[16px]">
                        {sectionsData?.basic?.map((fields) => {
                            return renderExportFields(fields, "ipr-export")
                        })}
                        <div className="mb-[15px] pt-[30px]" ref={iprChartRef}>
                            <IPRCurveGraph data={iprCurve ? iprCurve : defaultIPRData} />
                        </div>
                        {IPRSelection !== "ConstantPI" && IPRSelection !== "Composite" && sectionsData?.basic?.map((fields) => {
                            return renderFields(fields)
                        })}
                        {sectionsData?.basic?.map((fields) => {
                            return renderExportFields(fields, "depletion-export")
                        })}
                        <div className="mb-[15px] pt-[30px]" ref={depletionChartRef}>
                            <DepletionCurveGraph data={depletionCurve ? depletionCurve : defaultDepletionData} />
                        </div>
                    </div>
                )}
            />
        </div>
    );
};

export default BasicCalculations;